/**
 * Failure diagnosis.
 *
 * Raw esptool output ("Failed to connect with the device") tells a user nothing
 * about what to physically do next. Every message this tool can surface is
 * mapped here to: what happened, why it usually happens, and an ordered list of
 * things to try -- most likely fix first. The match patterns are taken from the
 * actual `throw` sites in esptool-js 0.6.x and from the DOMException names the
 * Web Serial spec defines, not from guesswork.
 */

export type ErrorCode =
  | "PORT_SELECTION_CANCELLED"
  | "PORT_BUSY"
  | "PORT_ALREADY_OPEN"
  | "PERMISSION_DENIED"
  | "CONNECT_FAILED"
  | "NO_SERIAL_DATA"
  | "SERIAL_NOISE"
  | "CHIP_UNKNOWN"
  | "STUB_FAILED"
  | "DEVICE_LOST"
  | "MD5_MISMATCH"
  | "DOES_NOT_FIT"
  | "FLASH_SIZE_UNSUPPORTED"
  | "INVALID_IMAGE"
  | "WRITE_FAILED"
  | "SECURE_DOWNLOAD_MODE"
  | "TIMEOUT"
  | "BAUD_UNSUPPORTED"
  | "UNKNOWN";

export type FlashPhase = "select-port" | "connect" | "erase" | "write" | "verify" | "reset" | "monitor";

export interface Diagnosis {
  code: ErrorCode;
  /** Short headline for the error banner. */
  title: string;
  /** Plain description of what the tool was doing and what went wrong. */
  what: string;
  /** The usual underlying cause. */
  why: string;
  /** Ordered remedies, most likely first. */
  fixes: string[];
  severity: "error" | "notice";
  /** The untouched message, kept for bug reports. */
  technical: string;
}

export interface DiagnoseContext {
  phase: FlashPhase;
  chipName?: string;
  baudRate?: number;
  /** True when the board exposes native USB (ESP32-S2/S3/C3 USB-CDC) rather than a UART bridge. */
  usingNativeUsb?: boolean;
}

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (error && typeof error === "object" && "message" in error) {
    return String((error as { message: unknown }).message);
  }
  return String(error);
}

function nameOf(error: unknown): string {
  if (error instanceof Error) return error.name;
  if (error && typeof error === "object" && "name" in error) {
    return String((error as { name: unknown }).name);
  }
  return "";
}

/** Steps that resolve the majority of "cannot talk to the board" reports. */
const BOOT_MODE_STEPS = [
  "Hold the BOOT (sometimes labelled IO0 or FLASH) button down, press and release EN/RST, then release BOOT. Now press Connect again. This forces the chip into its ROM download mode.",
  "Swap the USB cable for one you know carries data. Charge-only cables are the single most common cause -- the board powers up and its LED lights, but no serial device ever appears.",
  "Close anything else holding the port: the Arduino IDE Serial Monitor, PlatformIO, screen/minicom, or another browser tab running this flasher.",
  "Make sure you picked the right port in the browser dialog. On Windows it is a COM port; on macOS it looks like /dev/cu.usbserial-* or /dev/cu.usbmodem*; on Linux /dev/ttyUSB0 or /dev/ttyACM0.",
  "If no port is listed at all, the USB-to-serial driver is missing. Install the CP210x driver (Silicon Labs) or the CH340/CH341 driver depending on the chip next to the USB socket on your board.",
  "On Linux, add yourself to the dialout group (`sudo usermod -aG dialout $USER`) and log back in, otherwise the port exists but cannot be opened.",
];

interface Matcher {
  code: ErrorCode;
  /** Matched against the error message. */
  pattern?: RegExp;
  /** Matched against the DOMException name. */
  errorName?: string;
  /** Restrict to certain phases when a message is ambiguous. */
  phases?: FlashPhase[];
  build: (context: DiagnoseContext, raw: string) => Omit<Diagnosis, "code" | "technical">;
}

const MATCHERS: Matcher[] = [
  {
    code: "PORT_SELECTION_CANCELLED",
    errorName: "NotFoundError",
    phases: ["select-port", "connect"],
    build: () => ({
      severity: "notice",
      title: "No port was selected",
      what: "The browser's device chooser closed without a serial port being picked.",
      why: "Either Cancel was pressed, or the list was empty because no serial device is currently attached.",
      fixes: [
        "Press Connect again and choose your board from the list.",
        "If the list was empty, the board is not enumerating as a serial device. Check the cable carries data, then confirm the board appears in Windows Device Manager under Ports (COM & LPT), or run `ls /dev/tty.*` on macOS and Linux.",
        "Boards using a CP210x or CH340 USB-to-serial chip need their driver installed before any port appears.",
      ],
    }),
  },
  {
    code: "PORT_BUSY",
    errorName: "NetworkError",
    phases: ["select-port", "connect"],
    build: () => ({
      severity: "error",
      title: "The serial port could not be opened",
      what: "The port exists, but the browser was refused access to it.",
      why: "Almost always another program already has it open. A serial port can only be held by one process at a time.",
      fixes: [
        "Close the Arduino IDE Serial Monitor -- it keeps the port open even when idle.",
        "Close PlatformIO, screen, minicom, PuTTY, or any other terminal attached to the board.",
        "Close other browser tabs running this flasher.",
        "Unplug the board, wait two seconds, and plug it back in to clear a stuck handle.",
        "On Linux, a missing dialout group membership produces this same error. Run `sudo usermod -aG dialout $USER`, then log out and back in.",
      ],
    }),
  },
  {
    code: "PORT_ALREADY_OPEN",
    errorName: "InvalidStateError",
    build: () => ({
      severity: "error",
      title: "That port is already open in this page",
      what: "The flasher tried to open a connection it had not finished closing.",
      why: "A previous session was left half-open, usually after an error during a earlier flash.",
      fixes: [
        "Press Disconnect, then Connect again.",
        "If that does not clear it, reload the page and reconnect.",
      ],
    }),
  },
  {
    code: "PERMISSION_DENIED",
    errorName: "SecurityError",
    build: () => ({
      severity: "error",
      title: "Serial access was blocked by the browser",
      what: "The Web Serial permission request was denied before a port could be opened.",
      why: "The page is either not a secure context, or it is embedded in a frame that did not grant serial permission.",
      fixes: [
        "Open the flasher over HTTPS, or on http://localhost during development.",
        'If the page is inside an iframe, the parent must set allow="serial" on it.',
        "Check that the site is not blocked under the browser's site permissions for serial devices.",
      ],
    }),
  },
  {
    code: "NO_SERIAL_DATA",
    pattern: /no serial data received/i,
    build: (context) => ({
      severity: "error",
      title: "The board never answered",
      what: "The port opened successfully and the reset sequence was sent, but nothing at all came back from the board.",
      why: "The chip is not in download mode, or the data lines are not actually connected even though power is.",
      fixes: [
        ...BOOT_MODE_STEPS,
        context.usingNativeUsb
          ? "This board uses native USB. After a reset it disconnects and re-enumerates, so you may need to press Connect and pick the port again."
          : "Some boards need a 10 uF capacitor between EN and GND for the automatic reset circuit to work. Holding BOOT manually is the workaround.",
      ],
    }),
  },
  {
    code: "CONNECT_FAILED",
    pattern: /failed to connect with the device/i,
    build: (context) => ({
      severity: "error",
      title: `Could not enter download mode${context.chipName ? ` on the ${context.chipName}` : ""}`,
      what:
        "The flasher opened the port and tried the reset sequence several times, but the chip never responded to the " +
        "synchronisation handshake that puts it in flashing mode.",
      why:
        "The chip is running your existing sketch instead of its ROM bootloader. The automatic reset circuit (DTR/RTS) " +
        "either is not wired on this board, or something else is holding the port.",
      fixes: BOOT_MODE_STEPS,
    }),
  },
  {
    code: "SERIAL_NOISE",
    pattern: /invalid head of packet|possible serial noise or corruption|invalid slip escape/i,
    build: (context) => ({
      severity: "error",
      title: "The reply from the board was garbled",
      what: "Data came back from the board, but it was not a valid packet.",
      why:
        "Usually the baud rate is wrong for this adapter, the cable is picking up interference, or the thing on the " +
        "other end is not an ESP chip at all.",
      fixes: [
        `Lower the baud rate and try again${context.baudRate && context.baudRate > 115200 ? ` (currently ${context.baudRate} -- try 115200)` : " (115200 is the safest)"}.`,
        "Use a shorter, better-quality USB cable and avoid unpowered hubs -- plug directly into the computer.",
        "Confirm the port you chose really is the ESP board and not a different serial device such as a Bluetooth adapter or a 3D printer.",
        "If the board prints boot messages continuously, it may be stuck in a reset loop. Hold BOOT while connecting to silence it.",
      ],
    }),
  },
  {
    code: "CHIP_UNKNOWN",
    pattern: /unexpected chip magic value|failed to autodetect chip type|unsupported chip name/i,
    build: () => ({
      severity: "error",
      title: "The connected chip was not recognised",
      what: "The board responded, but the identifier it reported does not match any Espressif chip this tool knows about.",
      why: "Either the device is not an ESP chip, or it is a part newer than the bundled esptool-js.",
      fixes: [
        "Double-check the selected port is your ESP board.",
        "If this is a very new Espressif part, update the esptool-js dependency (`npm install esptool-js@latest`) and reload.",
        "A counterfeit or damaged chip can also report a bad magic value. Try another board to isolate it.",
      ],
    }),
  },
  {
    code: "STUB_FAILED",
    pattern: /failed to start stub|error loading stub json/i,
    build: () => ({
      severity: "error",
      title: "The flasher helper could not be loaded",
      what:
        "A small helper program (the flasher stub) is uploaded into the chip's RAM to speed up writing. It did not start.",
      why: "The chip may be fused into secure download mode, or the RAM upload was corrupted by a flaky connection.",
      fixes: [
        "Try again at a lower baud rate -- a bad connection corrupts the stub upload.",
        "If the chip has secure download mode enabled in eFuse, stub loading is permanently blocked and this board cannot be flashed from the browser.",
        "Power-cycle the board and retry with BOOT held.",
      ],
    }),
  },
  {
    code: "SECURE_DOWNLOAD_MODE",
    pattern: /secure download mode/i,
    build: () => ({
      severity: "error",
      title: "This chip is locked into secure download mode",
      what: "The chip only accepts signed images through a restricted protocol.",
      why: "Secure download mode has been burned into the chip's eFuses. That change is permanent and cannot be undone.",
      fixes: [
        "This board cannot be flashed with this tool. Use the vendor's signed-image tooling instead.",
        "If you did not intend to enable secure boot, use a different board -- the eFuse cannot be reverted.",
      ],
    }),
  },
  {
    code: "DEVICE_LOST",
    pattern: /device (has been )?lost|device lost detected|the device has been lost/i,
    build: (context) => ({
      severity: "error",
      title: "The board disconnected mid-operation",
      what: "The USB serial device vanished while the flasher was still talking to it.",
      why:
        context.phase === "write"
          ? "The board lost power or was unplugged during the write. Its flash is now partially written, so it will very likely not boot until a full flash is completed."
          : "The board was unplugged, reset, or lost power.",
      fixes: [
        "Reconnect the board and flash again from the start. A partial write leaves the flash inconsistent, so do not stop halfway.",
        "Plug directly into the computer rather than through a hub -- hubs frequently cannot supply the current an ESP32 draws when its radio starts.",
        "Check the cable is not loose or intermittent. Wiggling it during a flash will do exactly this.",
        "If the board resets on its own when Wi-Fi starts, its power supply is too weak. Use a better supply or a powered hub.",
      ],
    }),
  },
  {
    code: "MD5_MISMATCH",
    pattern: /md5 of file does not match/i,
    build: () => ({
      severity: "error",
      title: "Verification failed after writing",
      what:
        "Every region is read back and checksummed after writing. One region's checksum does not match the file that " +
        "was sent, so the data on the chip is not what you supplied.",
      why: "Either the flash chip is failing to hold data, or the link corrupted bytes in transit.",
      fixes: [
        "Flash again at a lower baud rate. Corruption in transit is the most common cause and a slower rate usually fixes it.",
        'Turn on "Erase entire flash" and retry -- worn or dirty flash sectors sometimes refuse to program without a full erase.',
        "Try a different USB cable and a direct port rather than a hub.",
        "If it fails repeatedly at the same address, the flash chip itself is likely worn out or damaged. Try another board to confirm.",
      ],
    }),
  },
  {
    code: "DOES_NOT_FIT",
    pattern: /doesn't fit in the available flash|does not fit in the available flash/i,
    build: (context) => ({
      severity: "error",
      title: "The firmware is too big for this board",
      what: "One of the files would run past the end of the chip's flash at the offset it was given.",
      why: `The board has less flash than the layout assumes${context.chipName ? `, or the offsets are wrong for the ${context.chipName}` : ""}.`,
      fixes: [
        "Check the offsets in the file list. An application accidentally placed at a high address is the usual cause.",
        'In the Arduino IDE, pick a partition scheme that fits: Tools > Partition Scheme > "Minimal" or "No OTA (2MB APP)".',
        "Confirm the board's real flash size. Many ESP32 modules sold as 16 MB are actually 4 MB.",
        'Set Flash size to "Detect" in the options so the true capacity is read from the chip.',
      ],
    }),
  },
  {
    code: "FLASH_SIZE_UNSUPPORTED",
    pattern: /is not supported by this chip type/i,
    build: () => ({
      severity: "error",
      title: "That flash size is not valid for this chip",
      what: "The requested flash size is not one this chip supports.",
      why: "A size was chosen manually that the connected part cannot address.",
      fixes: [
        'Set Flash size back to "Detect" so the actual size is read from the chip.',
        "If you need a fixed value, pick one within the chip's supported range shown in the log.",
      ],
    }),
  },
  {
    code: "INVALID_IMAGE",
    pattern: /invalid (firmware )?image magic|invalid image magic number|invalid segment count/i,
    build: () => ({
      severity: "error",
      title: "That file is not a valid firmware image",
      what: "The file does not begin with the 0xE9 magic byte that starts every ESP application image.",
      why: "The wrong file was almost certainly picked -- an .elf, a .hex, a partition CSV, or a truncated download.",
      fixes: [
        'Use the .bin files from Sketch > Export Compiled Binary in the Arduino IDE. The .elf and .map files next to them are not flashable.',
        "Check the file is not zero bytes or partially copied.",
        "If the binary came from a download, re-download it -- an interrupted transfer produces exactly this.",
      ],
    }),
  },
  {
    code: "WRITE_FAILED",
    pattern: /failed to write to target (flash|ram)|failed to (erase|write)/i,
    build: () => ({
      severity: "error",
      title: "A block failed to write",
      what: "The chip rejected or did not acknowledge a block of data, after the automatic retries were exhausted.",
      why: "Usually a marginal USB link; sometimes a worn flash sector.",
      fixes: [
        "Retry at a lower baud rate.",
        'Enable "Erase entire flash" and try once more.',
        "Change the USB cable and connect directly to the machine, not through a hub.",
        "Repeated failures at the same address point to failing flash hardware.",
      ],
    }),
  },
  {
    code: "BAUD_UNSUPPORTED",
    pattern: /baud rate change is not supported/i,
    build: () => ({
      severity: "error",
      title: "The board refused the faster baud rate",
      what: "The connection was made, but the chip would not switch to the requested speed.",
      why: "Some USB-to-serial bridges and secure-download-mode chips only run at 115200.",
      fixes: ["Set the baud rate to 115200 and flash again.", "Expect a slower write; this is otherwise harmless."],
    }),
  },
  {
    code: "TIMEOUT",
    pattern: /timed out|timeout/i,
    build: (context) => ({
      severity: "error",
      title: "The board stopped responding",
      what: `The flasher waited for a reply during ${context.phase === "erase" ? "the erase" : "the transfer"} and none arrived in time.`,
      why: "Erasing a large flash can legitimately take a while, but a silent chip usually means it reset or lost power.",
      fixes: [
        "Retry at a lower baud rate.",
        "Plug directly into the computer instead of a hub, so the board gets enough current.",
        "Hold BOOT and reset the board, then flash again.",
        "If it always stops at the same point, try with a different USB cable.",
      ],
    }),
  },
];

export function diagnose(error: unknown, context: DiagnoseContext): Diagnosis {
  const raw = messageOf(error);
  const errorName = nameOf(error);

  for (const matcher of MATCHERS) {
    if (matcher.phases && !matcher.phases.includes(context.phase)) continue;

    const nameHit = matcher.errorName !== undefined && matcher.errorName === errorName;
    const messageHit = matcher.pattern !== undefined && matcher.pattern.test(raw);

    if (nameHit || messageHit) {
      return { code: matcher.code, technical: raw, ...matcher.build(context, raw) };
    }
  }

  return {
    code: "UNKNOWN",
    severity: "error",
    title: "The flash did not complete",
    what: `An unexpected error occurred while ${describePhase(context.phase)}.`,
    why: "This is not a failure mode the tool recognises, so the underlying message is shown below verbatim.",
    fixes: [
      "Retry at a lower baud rate -- it resolves a surprising share of unclassified failures.",
      "Hold BOOT, tap EN/RST, release BOOT, then connect and flash again.",
      "Try a different USB cable and a direct port rather than a hub.",
      "Open the log panel and copy the full output if you need to report this.",
    ],
    technical: raw,
  };
}

function describePhase(phase: FlashPhase): string {
  switch (phase) {
    case "select-port":
      return "choosing a serial port";
    case "connect":
      return "connecting to the board";
    case "erase":
      return "erasing the flash";
    case "write":
      return "writing firmware";
    case "verify":
      return "verifying the written data";
    case "reset":
      return "resetting the board";
    case "monitor":
      return "reading the serial output";
  }
}
